// src/components/BookingDashboard.tsx
import { useState, useEffect } from 'react';
import { bookingsAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { MessagingIconSimple } from './messaging/MessagingIconSimple.tsx';

interface Booking {
  id: string;
  customer: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    profile_picture: string;
  };
  trainer: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    profile_picture: string;
  };
  scheduled_date: string;
  scheduled_time: string;
  duration_minutes: number;
  total_amount: number;
  platform_fee: number;
  trainer_amount: number;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  notes: string;
  customer_address: string;
  created_at: string;
}

export function BookingDashboard() {
  const { user } = useAuth();
  const [allBookings, setAllBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past'>('upcoming');
  const [cancellingBooking, setCancellingBooking] = useState<string | null>(null);
  const [processingBooking, setProcessingBooking] = useState<string | null>(null);
  const [showDeclineModal, setShowDeclineModal] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState<string>('');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await bookingsAPI.getMyBookings();
      const bookings: Booking[] = response.data.bookings || [];

      // Debug logging
      console.log('Current user:', user);
      console.log('All bookings from API:', bookings);
      console.log('User ID type:', typeof user?.id, 'User ID value:', user?.id);

      // Check each booking's customer data
      bookings.forEach((booking, index) => {
        console.log(`Booking ${index}:`, {
          bookingId: booking.id,
          customerId: booking.customer.id,
          customerIdType: typeof booking.customer.id,
          customerUsername: booking.customer.username,
          customerName: `${booking.customer.first_name} ${booking.customer.last_name}`,
          trainerName: `${booking.trainer.first_name} ${booking.trainer.last_name}`
        });
      });

      setAllBookings(bookings);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter bookings based on user role and ID
  const getUserBookings = () => {
    if (!user) return [];

    return allBookings.filter(booking => {
      if (user.role === 'customer') {
        // Clients see only bookings they made
        return booking.customer.id === user.id || booking.customer.username === user.username;
      } else if (user.role === 'trainer') {
        // Trainers see only bookings for their services
        return booking.trainer.id === user.id || booking.trainer.username === user.username;
      } else if (user.role === 'admin') {
        // Admins see all bookings
        return true;
      }
      return false;
    });
  };

  const handleCancelBooking = async (bookingId: string) => {
    setCancellingBooking(bookingId);
    try {
      await bookingsAPI.cancelBooking(bookingId);
      // Refresh bookings
      await fetchBookings();
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      alert('Failed to cancel booking. Please try again.');
    } finally {
      setCancellingBooking(null);
    }
  };

  const handleAcceptBooking = async (bookingId: string) => {
    setProcessingBooking(bookingId);
    try {
      await bookingsAPI.acceptBooking(bookingId);
      await fetchBookings(); // Refresh the bookings list
      alert('Booking accepted successfully!');
    } catch (error) {
      console.error('Failed to accept booking:', error);
      alert('Failed to accept booking. Please try again.');
    } finally {
      setProcessingBooking(null);
    }
  };

  const handleDeclineBooking = async (bookingId: string) => {
    setProcessingBooking(bookingId);
    try {
      await bookingsAPI.declineBooking(bookingId, declineReason);
      await fetchBookings(); // Refresh the bookings list
      setShowDeclineModal(null);
      setDeclineReason('');
      alert('Booking declined successfully.');
    } catch (error) {
      console.error('Failed to decline booking:', error);
      alert('Failed to decline booking. Please try again.');
    } finally {
      setProcessingBooking(null);
    }
  };

  const getFilteredBookings = () => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const userBookings = getUserBookings();

    return userBookings.filter(booking => {
      switch (filter) {
        case 'upcoming':
          return booking.scheduled_date >= today && ['pending', 'confirmed'].includes(booking.status);
        case 'past':
          return booking.scheduled_date < today || ['completed', 'cancelled', 'no_show'].includes(booking.status);
        case 'all':
        default:
          return true;
      }
    });
  };

  const formatDate = (dateString: string) => {
    // Parse the date string manually to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);

    // Create date object in local timezone (month is 0-indexed)
    const date = new Date(year, month - 1, day);

    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-[#F8F4EE] text-[#E6A756] border border-[#E6A756]';
      case 'confirmed': return 'bg-[#B8C5A6] text-[#5D4037]';
      case 'completed': return 'bg-[#8B5A3C] text-[#FEFCF8]';
      case 'cancelled': return 'bg-[#F5EFE7] text-[#A0673D] border border-[#D4C4B0]';
      case 'no_show': return 'bg-[#F8F4EE] text-[#8D6E63] border border-[#D4C4B0]';
      default: return 'bg-[#F8F4EE] text-[#8D6E63] border border-[#D4C4B0]';
    }
  };

  const canCancelBooking = (booking: Booking) => {
    const bookingDate = new Date(`${booking.scheduled_date}T${booking.scheduled_time}`);
    const now = new Date();
    const hoursUntilBooking = (bookingDate.getTime() - now.getTime()) / (1000 * 60 * 60);

    return ['pending', 'confirmed'].includes(booking.status) && hoursUntilBooking > 24;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse space-y-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white/60 rounded-lg p-6 h-32"></div>
          ))}
        </div>
      </div>
    );
  }

  const filteredBookings = getFilteredBookings();
  const userBookings = getUserBookings();

  // Get role-specific title and description
  const getDashboardTitle = () => {
    if (user?.role === 'trainer') {
      return {
        title: 'My Training Sessions',
        description: 'Manage your training sessions and client bookings'
      };
    } else if (user?.role === 'admin') {
      return {
        title: 'All Platform Bookings',
        description: 'View and manage all bookings across the platform'
      };
    } else {
      return {
        title: 'My Bookings',
        description: 'Manage your training sessions and view booking history'
      };
    }
  };

  const { title, description } = getDashboardTitle();

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">{title}</h1>
        <p className="text-gray-600">{description}</p>
        {user?.role && (
          <div className="mt-2 flex items-center space-x-2">
            <span className="text-sm text-gray-500">Viewing as:</span>
            <span className="px-2 py-1 bg-[#F8F4EE] text-[#5D4037] text-xs rounded-full capitalize border border-[#D4C4B0]">
              {user.role}
            </span>
            <span className="text-sm text-gray-500">
              ({userBookings.length} total {user.role === 'trainer' ? 'sessions' : 'bookings'})
            </span>
          </div>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        {[
          { key: 'upcoming', label: 'Upcoming' },
          { key: 'past', label: 'Past' },
          { key: 'all', label: 'All' }
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key as any)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === tab.key
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Bookings List */}
      <div className="space-y-4">
        {filteredBookings.length === 0 ? (
          <div className="bg-white/60 backdrop-blur-sm rounded-lg p-8 text-center border border-white/40">
            <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings found</h3>
            <p className="text-gray-600">
              {filter === 'upcoming' && user?.role === 'trainer' && "No upcoming training sessions."}
              {filter === 'upcoming' && user?.role !== 'trainer' && "You don't have any upcoming sessions."}
              {filter === 'past' && user?.role === 'trainer' && "No past training sessions."}
              {filter === 'past' && user?.role !== 'trainer' && "You don't have any past sessions."}
              {filter === 'all' && user?.role === 'trainer' && "No training sessions found."}
              {filter === 'all' && user?.role !== 'trainer' && "You haven't booked any sessions yet."}
            </p>
          </div>
        ) : (
          filteredBookings.map((booking) => (
            <div key={booking.id} className="bg-white/60 backdrop-blur-sm rounded-lg p-6 border border-white/40 shadow-sm">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-gray-300 rounded-full overflow-hidden">
                      {user?.role === 'trainer' ? (
                        // Trainers see client info
                        booking.customer.profile_picture ? (
                          <img
                            src={booking.customer.profile_picture}
                            alt={`${booking.customer.first_name} ${booking.customer.last_name}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback to initials if image fails to load
                              e.currentTarget.style.display = 'none';
                              const fallbackElement = e.currentTarget.nextElementSibling as HTMLElement;
                              if (fallbackElement) {
                                fallbackElement.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null
                      ) : (
                        // Clients see trainer info
                        booking.trainer.profile_picture ? (
                          <img
                            src={booking.trainer.profile_picture}
                            alt={`${booking.trainer.first_name} ${booking.trainer.last_name}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback to initials if image fails to load
                              e.currentTarget.style.display = 'none';
                              const fallbackElement = e.currentTarget.nextElementSibling as HTMLElement;
                              if (fallbackElement) {
                                fallbackElement.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null
                      )}

                      {/* Fallback initials - shown when no profile picture exists or image fails to load */}
                      <div
                        className={`w-full h-full flex items-center justify-center text-white font-bold ${user?.role === 'trainer'
                          ? (booking.customer.profile_picture ? 'hidden' : 'bg-gradient-to-br from-[#9CAF88] to-[#7A8471]')
                          : (booking.trainer.profile_picture ? 'hidden' : 'bg-gradient-to-br from-[#E6A756] to-[#D2824C]')
                          }`}
                      >
                        {user?.role === 'trainer'
                          ? `${booking.customer.first_name[0]}${booking.customer.last_name[0]}`
                          : `${booking.trainer.first_name[0]}${booking.trainer.last_name[0]}`
                        }
                      </div>
                    </div>
                    <div>
                      {user?.role === 'trainer' ? (
                        <>
                          <h3 className="font-semibold text-gray-900">
                            {booking.customer.first_name} {booking.customer.last_name}
                          </h3>
                          <p className="text-sm text-gray-600">@{booking.customer.username}</p>
                        </>
                      ) : (
                        <>
                          <h3 className="font-semibold text-gray-900">
                            {booking.trainer.first_name} {booking.trainer.last_name}
                          </h3>
                          <p className="text-sm text-gray-600">@{booking.trainer.username}</p>
                        </>
                      )}


                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                      {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600">Date</p>
                      <p className="font-medium">{formatDate(booking.scheduled_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Time</p>
                      <p className="font-medium">{formatTime(booking.scheduled_time)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Duration</p>
                      <p className="font-medium">{booking.duration_minutes} minutes</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {user?.role === 'trainer' ? 'Your Earnings' : 'Total Cost'}
                      </p>
                      <p className="font-medium">
                        ${user?.role === 'trainer' ? booking.trainer_amount : booking.total_amount}
                      </p>
                    </div>
                  </div>

                  {booking.notes && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600">Notes</p>
                      <p className="text-gray-800">{booking.notes}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600">Location</p>
                      <p className="text-gray-800">{booking.customer_address}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {user?.role === 'trainer' ? "Client's Phone" : "Trainer's Phone"}
                      </p>
                      <p className="text-gray-800 font-medium">
                        {user?.role === 'trainer'
                          ? booking.customer.phone_number
                          : booking.trainer.phone_number
                        }
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col space-y-2 ml-4">
                  {user?.role === 'customer' && canCancelBooking(booking) && (
                    <button
                      onClick={() => handleCancelBooking(booking.id)}
                      disabled={cancellingBooking === booking.id}
                      className="px-4 py-2 text-sm text-[#A0673D] hover:text-[#8B5A3C] hover:bg-[#F8F4EE] rounded-lg transition-colors disabled:opacity-50 font-medium"
                    >
                      {cancellingBooking === booking.id ? 'Cancelling...' : 'Cancel'}
                    </button>

                  )}

                  {user?.role === 'trainer' && booking.status === 'pending' && (
                    <div className="flex flex-col space-y-2">
                      <button
                        onClick={() => handleAcceptBooking(booking.id)}
                        disabled={processingBooking === booking.id}
                        className="px-4 py-2 text-sm text-[#7A8471] hover:text-[#5D4037] hover:bg-[#B8C5A6] rounded-lg transition-colors disabled:opacity-50 font-medium"
                      >
                        {processingBooking === booking.id ? 'Accepting...' : 'Accept'}
                      </button>

                      <button
                        onClick={() => setShowDeclineModal(booking.id)}
                        disabled={processingBooking === booking.id}
                        className="px-4 py-2 text-sm text-[#A0673D] hover:text-[#8B5A3C] hover:bg-[#F8F4EE] rounded-lg transition-colors disabled:opacity-50 font-medium"
                      >
                        Decline
                      </button>
                    </div>
                  )}

                  {booking.status === 'completed' && (
                    <button className="px-4 py-2 text-sm text-[#8B5A3C] hover:text-[#5D4037] hover:bg-[#F8F4EE] rounded-lg transition-colors font-medium">
                      {user?.role === 'trainer' ? 'View Review' : 'Leave Review'}
                    </button>
                  )}

                  {['confirmed', 'pending'].includes(booking.status) && (
                    <div className="flex items-center space-x-2 px-3 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors">
                      {user?.role === 'trainer' ? (
                        <>
                          <MessagingIconSimple
                            targetUser={{
                              id: booking.customer.id,
                              username: booking.customer.username,
                              first_name: booking.customer.first_name,
                              last_name: booking.customer.last_name,
                              profile_picture: booking.customer.profile_picture
                            }}
                            className="bg-transparent border-none hover:bg-white/20"
                          />
                          <span className="text-sm text-gray-600">Message</span>
                        </>
                      ) : (
                        <>
                          <MessagingIconSimple
                            targetUser={{
                              id: booking.trainer.id,
                              username: booking.trainer.username,
                              first_name: booking.trainer.first_name,
                              last_name: booking.trainer.last_name,
                              profile_picture: booking.trainer.profile_picture
                            }}
                            className="bg-transparent border-none hover:bg-white/20"
                          />
                          <span className="text-sm text-gray-600">Message</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {!canCancelBooking(booking) && ['pending', 'confirmed'].includes(booking.status) && user?.role === 'customer' && (
                <div className="mt-4 p-3 bg-[#F8F4EE] border border-[#E6A756] rounded-lg">
                  <p className="text-sm text-[#A0673D]">
                    Cancellation is only allowed up to 24 hours before the session.
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Summary Stats */}
      {userBookings.length > 0 && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 text-center border border-white/40">
            <div className="text-2xl font-bold text-gray-900">
              {userBookings.filter(b => b.status === 'completed').length}
            </div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 text-center border border-white/40">
            <div className="text-2xl font-bold text-gray-900">
              {userBookings.filter(b => ['pending', 'confirmed'].includes(b.status)).length}
            </div>
            <div className="text-sm text-gray-600">Upcoming</div>
          </div>
          <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 text-center border border-white/40">
            <div className="text-2xl font-bold text-gray-900">
              ${userBookings.filter(b => b.status === 'completed').reduce((sum, b) =>
                sum + (user?.role === 'trainer' ? b.trainer_amount : b.total_amount), 0
              ).toFixed(0)}
            </div>
            <div className="text-sm text-gray-600">
              {user?.role === 'trainer' ? 'Total Earned' : 'Total Spent'}
            </div>
          </div>
          <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 text-center border border-white/40">
            <div className="text-2xl font-bold text-gray-900">
              {user?.role === 'trainer'
                ? new Set(userBookings.map(b => b.customer.id)).size
                : new Set(userBookings.map(b => b.trainer.id)).size
              }
            </div>
            <div className="text-sm text-gray-600">
              {user?.role === 'trainer' ? 'Clients' : 'Trainers'}
            </div>
          </div>
        </div>
      )}

      {/* Decline Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Decline Booking</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to decline this booking? You can optionally provide a reason for the customer.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="e.g., Schedule conflict, unavailable at this time..."
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeclineModal(null);
                  setDeclineReason('');
                }}
                className="flex-1 bg-[#F8F4EE] text-[#5D4037] py-2 rounded-lg hover:bg-[#F5EFE7] transition-colors font-medium border border-[#D4C4B0]"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeclineBooking(showDeclineModal)}
                disabled={processingBooking === showDeclineModal}
                className="flex-1 bg-[#A0673D] text-[#FEFCF8] py-2 rounded-lg hover:bg-[#8B5A3C] transition-colors disabled:opacity-50 font-medium"
              >
                {processingBooking === showDeclineModal ? 'Declining...' : 'Decline Booking'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}